// Leitura da 1ª coluna de uma planilha (.xlsx ou .csv) — usada pra importar
// listas de códigos.
//
// Leitor próprio de propósito: só precisamos de uma coluna, e as bibliotecas de
// Excel ou têm falha de segurança conhecida (o `xlsx` do npm) ou leem num Web
// Worker montado em tempo de execução, frágil no build do react-scripts. O .xlsx
// é um zip de XMLs: o fflate abre o zip e o DOMParser do navegador lê o XML.
import { unzipSync, strFromU8 } from 'fflate';

const TAMANHO_MAXIMO = 5 * 1024 * 1024; // 5 MB — lista de códigos é bem menor que isso

// Casa a tag pelo nome local, com ou sem prefixo (alguns sistemas gravam <x:c>).
const tags = (el, nome) => (el ? [...el.getElementsByTagNameNS('*', nome)] : []);

const lerXml = (arquivos, caminho) => {
  const bytes = arquivos[caminho];
  return bytes ? new DOMParser().parseFromString(strFromU8(bytes), 'application/xml') : null;
};

// "B12" → 1 (A = 0)
const indiceColuna = (ref) => {
  let n = 0;
  for (const ch of String(ref).replace(/[^A-Z]/gi, '').toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
};

// A 1ª aba nem sempre é sheet1.xml: segue workbook.xml → relação → arquivo.
function caminhoPrimeiraAba(arquivos) {
  const aba = tags(lerXml(arquivos, 'xl/workbook.xml'), 'sheet')[0];
  const rid = aba && [...aba.attributes].find(a => a.localName === 'id')?.value;
  const rel = tags(lerXml(arquivos, 'xl/_rels/workbook.xml.rels'), 'Relationship').find(r => r.getAttribute('Id') === rid);
  if (!rel) return 'xl/worksheets/sheet1.xml';
  const alvo = rel.getAttribute('Target').replace(/^\//, '');
  return alvo.startsWith('xl/') ? alvo : `xl/${alvo}`;
}

// Texto de uma célula/string compartilhada, ignorando a guia fonética (<rPh>).
const textoDe = (el) => tags(el, 't').filter(t => t.parentNode?.localName !== 'rPh').map(t => t.textContent).join('');

function lerXlsx(bytes) {
  let arquivos;
  try {
    arquivos = unzipSync(bytes);
  } catch {
    throw new Error('Não consegui abrir a planilha. Confira se o arquivo é um .xlsx válido.');
  }
  // Texto repetido fica numa tabela compartilhada; a célula guarda só o índice.
  const compartilhados = tags(lerXml(arquivos, 'xl/sharedStrings.xml'), 'si').map(textoDe);
  const aba = lerXml(arquivos, caminhoPrimeiraAba(arquivos));
  if (!aba) throw new Error('Não achei nenhuma aba com dados na planilha.');

  const valores = [];
  for (const linha of tags(aba, 'row')) {
    const celulas = tags(linha, 'c');
    const c = celulas.find(x => (x.getAttribute('r') ? indiceColuna(x.getAttribute('r')) === 0 : x === celulas[0]));
    if (!c) continue;
    const tipo = c.getAttribute('t');
    const v = tags(c, 'v')[0]?.textContent;
    const valor = tipo === 's' ? compartilhados[parseInt(v, 10)]
      : tipo === 'inlineStr' ? textoDe(tags(c, 'is')[0])
      : v;
    if (valor != null && String(valor).trim()) valores.push(String(valor).trim());
  }
  return valores;
}

function lerCsv(texto) {
  return texto.split(/\r?\n/)
    .map(l => l.split(/[;,\t]/)[0].replace(/^﻿/, '').replace(/^"|"$/g, '').trim())
    .filter(Boolean);
}

/** Devolve os valores (texto) da 1ª coluna da 1ª aba, na ordem da planilha. */
export async function lerPrimeiraColuna(file) {
  const nome = (file?.name || '').toLowerCase();
  if (file.size > TAMANHO_MAXIMO) throw new Error('Arquivo grande demais (máx. 5 MB).');
  if (nome.endsWith('.csv') || nome.endsWith('.txt')) return lerCsv(await file.text());
  if (nome.endsWith('.xlsx')) return lerXlsx(new Uint8Array(await file.arrayBuffer()));
  if (nome.endsWith('.xls')) throw new Error('Esse é o formato antigo do Excel (.xls). Abra no Excel e salve como .xlsx.');
  throw new Error('Formato não suportado. Use uma planilha .xlsx ou .csv.');
}
